import { Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import dayjs from "dayjs";
import {
  setIsChangingSeat,
  setOriginalEventId,
  setOriginalTickets,
  setSelectedEvent,
  setSelectedSubscription,
} from "features/slices/reservationFlowSlice";
import {
  EventDetails,
  GroupTicketDetails,
  SeatDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import i18n from "plugins/localization";
import { useFetchEventAvailablitiesQuery } from "queries/ticket.queries";
import React, { useState } from "react";
import { toScehdule, toSeatSelection } from "utils/navigationUtils";
import GroupDialog from "./GroupDialog";
import { isTicketReservedByGuestComponent } from "./helpers";

interface EventActionsProps {
  seat: SeatDetails;
  event: EventDetails;
  groupCode: string;
  releaseAllBtnPress: () => void;
  tickets: TicketDetails[];
  groupTickets: GroupTicketDetails[];
}

const isTicketReservedByPrimaryComponent = (ticket: GroupTicketDetails) => {
  return ["BANK", "RESERVATION", "VOUCHER"].includes(
    ticket.planPackageComponentType
  );
};

const EventActions: React.FC<EventActionsProps> = (props) => {
  // props
  const { seat, event, groupCode, releaseAllBtnPress, groupTickets, tickets } =
    props;
  // custom hooks
  const { account } = useAccount();

  // local states
  const [groupDialog, setGroupDialog] = useState(false);

  // find the plan package id that was used to reserve the current event
  const planPackageId = groupTickets.find(
    isTicketReservedByPrimaryComponent
  )?.planPackageId;

  if (!planPackageId) {
    return null;
  }

  // find the subscription that was used to reserve the current event
  const subscription = account?.subscriptions
    .filter((sub) => !sub.expired)
    .filter((sub) => sub.planPackage.id === planPackageId)[0];

  // react queries
  const { refetch: fetchEventAvailablities } = useFetchEventAvailablitiesQuery({
    accountId: account!.id,
    planPackageId,
  });

  // redux
  const dispatch = useAppDispatch();

  // utilities variables
  const isStanardSeating = seat.premiumCode === null;

  const isNotGroupSeats =
    groupTickets.filter((t) => t.status === "RESERVED").length ===
    tickets.length;

  const showCancelAllBtn =
    !subscription?.planPackage.eventCancelDisabled &&
    groupTickets
      .filter((groupTicket) => groupTicket.status !== "HOLD")
      .filter((groupTicket) => groupTicket.reservedByAccountId === account?.id)
      .every((ticket) => {
        if (
          // if account has guest pass that is claimed by someone
          isTicketReservedByGuestComponent(ticket.planPackageComponentType) &&
          ticket.accountId !== account?.id
        ) {
          return false;
        }
        if (ticket.accountId === account?.id && ticket.status === "RESERVED") {
          return true;
        }
        return false;
      });

  const handleChangeSeatsClick = async () => {
    const eventAvailablities = (await fetchEventAvailablities()).data;
    const evt = eventAvailablities?.find((evt) => evt.eventId === event.id);
    if (evt) {
      dispatch(setSelectedSubscription(subscription!));
      dispatch(setIsChangingSeat(true));
      dispatch(setSelectedEvent(evt));
      dispatch(setOriginalTickets(tickets));
      toSeatSelection();
    }
  };

  const handleSwitchEventClick = async () => {
    dispatch(setSelectedSubscription(subscription!));
    dispatch(setIsChangingSeat(true));
    dispatch(setOriginalTickets(tickets));
    dispatch(setOriginalEventId(event.id));
    toScehdule();
  };

  const ticketProvider = tickets[0].provider;
  const minuteAfterReservation = dayjs()
    .utc()
    .diff(tickets[0].reservedAt, "minutes");

  if (ticketProvider === "AXS" && minuteAfterReservation < 30) {
    return (
      <Text category="h6">
        {`Your reservation can be modified after ${i18n.t("common.minute", {
          count: 30 - minuteAfterReservation,
        })}`}
      </Text>
    );
  }

  return (
    <>
      <BaseCard title={i18n.t("screens.EventDetail.eventActions")}>
        {isStanardSeating && (
          <BaseCardListItem
            title={i18n.t("screens.EventDetail.sitWithFriends")}
            onPress={() => setGroupDialog(true)}
          ></BaseCardListItem>
        )}
        {isStanardSeating && isNotGroupSeats && subscription !== undefined && (
          <>
            <BaseCardListItem
              title="Upgrade/Add-on Seats"
              onPress={handleChangeSeatsClick}
            ></BaseCardListItem>
            <BaseCardListItem
              title="Exchange Event"
              onPress={handleSwitchEventClick}
            ></BaseCardListItem>
          </>
        )}
        {showCancelAllBtn && (
          <BaseCardListItem
            title="Cancel Booking"
            onPress={releaseAllBtnPress}
            testID="release-all-button"
          ></BaseCardListItem>
        )}
      </BaseCard>
      <GroupDialog
        visible={groupDialog}
        setVisible={setGroupDialog}
        seat={seat}
        event={event}
        groupCode={groupCode}
        onCancel={() => setGroupDialog(false)}
      />
    </>
  );
};

export default EventActions;
